import { useEffect, useState } from 'preact/hooks';
import { DashboardApi, HttpError, type SsoConfig } from '../api.js';
import { type AuthOrg, type AuthUser, logout } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

export function Settings({
  user,
  org,
  onSignOut,
}: {
  user: AuthUser;
  org: AuthOrg;
  onSignOut: () => void;
}): preact.ComponentChildren {
  const [sso, setSso] = useState<SsoConfig | null>(null);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [allowedDomain, setAllowedDomain] = useState('');
  const [autoRole, setAutoRole] = useState<'admin' | 'editor' | 'viewer'>('editor');
  const [enforce, setEnforce] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState<boolean | null>(null);
  const [notif, setNotif] = useState<{ regressionEmail: boolean; completionEmail: boolean } | null>(
    null,
  );

  useEffect(() => {
    void (async () => {
      try {
        const p = await api.getDigestPref(org.id);
        setDigestEnabled(p.enabled);
        setNotif(await api.getNotificationSettings(org.id));
      } catch {
        /* dev backends without DB return 500 */
      }
    })();
  }, [org.id]);

  const toggleDigest = async (next: boolean) => {
    setDigestEnabled(next);
    try {
      await api.setDigestPref(org.id, next);
    } catch {
      setDigestEnabled(!next);
    }
  };

  const toggleNotif = async (key: 'regressionEmail' | 'completionEmail', next: boolean) => {
    if (!notif) return;
    const updated = { ...notif, [key]: next };
    setNotif(updated);
    try {
      await api.setNotificationSettings(org.id, updated);
    } catch {
      setNotif(notif);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const s = await api.getSso(org.id);
        setSso(s);
        if (s.enabled) {
          setAllowedDomain(s.allowedDomain);
          setAutoRole(s.autoAssignRole);
          setEnforce(s.enforce);
        }
      } catch {
        /* SSO endpoint may be unavailable on dev */
      }
    })();
  }, [org.id]);

  const saveSso = async () => {
    setSsoError(null);
    try {
      await api.updateSso(org.id, {
        allowedDomain: allowedDomain.trim().toLowerCase(),
        autoAssignRole: autoRole,
        enforce,
      });
      setSso({
        enabled: true,
        provider: 'google',
        allowedDomain: allowedDomain.trim().toLowerCase(),
        autoAssignRole: autoRole,
        enforce,
      });
    } catch (e) {
      setSsoError(e instanceof HttpError ? e.message : String(e));
    }
  };

  const disableSso = async () => {
    if (!confirm('Disable SSO for this org? Members will need to sign in with magic links.'))
      return;
    setSsoError(null);
    try {
      await api.disableSso(org.id);
      setSso({ enabled: false });
      setAllowedDomain('');
    } catch (e) {
      setSsoError(e instanceof HttpError ? e.message : String(e));
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      onSignOut();
    }
  };

  return (
    <>
      <div class="page-header">
        <h1>Settings</h1>
        <p>Account + organization profile.</p>
      </div>

      <div class="card">
        <h2>You</h2>
        <div class="meta">
          email: <code>{user.email}</code>
        </div>
        <div class="meta">
          user id: <code>{user.id}</code>
        </div>
        <div class="meta">joined: {new Date(user.createdAt).toLocaleDateString()}</div>
        <button
          type="button"
          class="secondary"
          style={{ marginTop: 16 }}
          onClick={() => void handleSignOut()}
        >
          Sign out
        </button>
      </div>

      <div class="card">
        <h2>Organization</h2>
        <div class="meta">
          name: <strong>{org.name}</strong>
        </div>
        <div class="meta">
          slug: <code>{org.slug}</code>
        </div>
        <div class="meta">
          plan: <code>{org.plan}</code> · status: <code>{org.status}</code>
        </div>
        <div class="field" style={{ marginTop: 16 }}>
          <label htmlFor="org-region">Region (data residency)</label>
          <select
            id="org-region"
            defaultValue={org.region}
            onChange={async (e) => {
              const next = (e.target as HTMLSelectElement).value as 'us' | 'eu' | 'apac';
              try {
                await api.updateOrgRegion(org.id, next);
                window.location.reload();
              } catch {
                (e.target as HTMLSelectElement).value = org.region;
              }
            }}
          >
            <option value="us">US (default) — api.wholisphere.ai</option>
            <option value="eu" disabled={org.region !== 'eu'}>
              EU — eu.api.wholisphere.ai (coming soon)
            </option>
            <option value="apac" disabled={org.region !== 'apac'}>
              APAC — apac.api.wholisphere.ai (coming soon)
            </option>
          </select>
          <p class="small muted">
            Owners only. Region pinning is coming soon — this setting records your preference and
            does not move any data yet; all data is currently stored in the US region.
          </p>
        </div>
      </div>

      {sso !== null && (
        <div class="card">
          <h2>Single sign-on (Google Workspace)</h2>
          <p class="desc">
            Members of your Google Workspace can sign in with their Google account. We auto-join
            them to this organization with the role you set below.
          </p>
          {ssoError && <Banner variant="warn">{ssoError}</Banner>}
          <div class="field">
            <label htmlFor="sso-domain">Allowed domain</label>
            <input
              id="sso-domain"
              type="text"
              placeholder="company.com"
              value={allowedDomain}
              onInput={(e) => setAllowedDomain((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="field">
            <label htmlFor="sso-role">Auto-assigned role</label>
            <select
              id="sso-role"
              value={autoRole}
              onChange={(e) =>
                setAutoRole((e.target as HTMLSelectElement).value as 'admin' | 'editor' | 'viewer')
              }
            >
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <div class="field">
            <label>
              <input
                type="checkbox"
                checked={enforce}
                onChange={(e) => setEnforce((e.target as HTMLInputElement).checked)}
              />{' '}
              Enforce SSO (block magic-link sign-in for this domain)
            </label>
          </div>
          <button type="button" disabled={!allowedDomain.trim()} onClick={() => void saveSso()}>
            {sso.enabled ? 'Update SSO' : 'Enable SSO'}
          </button>
          {sso.enabled && (
            <button
              type="button"
              class="secondary"
              style={{ marginLeft: 8 }}
              onClick={() => void disableSso()}
            >
              Disable SSO
            </button>
          )}
          {sso.enabled && (
            <p class="small muted" style={{ marginTop: 8 }}>
              Sign-in URL for testing:{' '}
              <code>/v1/auth/sso/google/start?domain={sso.allowedDomain}</code>
            </p>
          )}
        </div>
      )}

      {digestEnabled !== null && (
        <div class="card">
          <h2>Notifications</h2>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={digestEnabled}
              onChange={(e) => void toggleDigest((e.target as HTMLInputElement).checked)}
            />
            <span>Email me a weekly digest of usage + incidents.</span>
          </label>
          <p class="small muted" style={{ marginTop: 8 }}>
            Sent every ~7 days. Includes top capabilities, weekly burn, and any open incidents.
          </p>
          {notif && (
            <div
              style={{
                marginTop: 16,
                borderTop: '1px solid var(--border, #2a3140)',
                paddingTop: 16,
              }}
            >
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={notif.regressionEmail}
                  onChange={(e) =>
                    void toggleNotif('regressionEmail', (e.target as HTMLInputElement).checked)
                  }
                />
                <span>Email the team when a scheduled scan regresses.</span>
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={notif.completionEmail}
                  onChange={(e) =>
                    void toggleNotif('completionEmail', (e.target as HTMLInputElement).checked)
                  }
                />
                <span>Email the team every time a scheduled scan completes.</span>
              </label>
              <p class="small muted" style={{ marginTop: 8 }}>
                Alerts go to owners, admins, and editors — no webhook receiver needed.
              </p>
            </div>
          )}
        </div>
      )}

      <div class="card">
        <h2>Danger zone</h2>
        <p class="desc">
          Permanently delete this organization, all members, all API keys, and the audit log. This
          cannot be undone. (Stripe records retained for tax purposes.)
        </p>
        <button type="button" class="danger" disabled>
          Delete organization (contact support)
        </button>
      </div>
    </>
  );
}
